import React, { useState, useEffect, useMemo, useRef } from 'react';
import DateTimeRangePicker from '@wojtekmaj/react-datetimerange-picker';
import '@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';

import {
  Play,
  Pause,
  RotateCcw,
  Activity,
  Settings,
  Calendar,
  Clock,
  Layers,
  MapPin,
  SkipBack,
  SkipForward,
  Navigation,
  Eye,
  EyeOff,
  Map,
  Loader,
  AlertCircle,
  Gauge,
  ChevronDown,
  ArrowRight,
  Crosshair,
} from 'lucide-react';
import { useOcean } from '../../contexts/OceanDataContext';
import { getVesselIconComponent } from '../../utils/vesselIcons';
import { VESSEL_DISPLAY_NONE } from '../../utils/vesselUtils';

// Configuration for all map layer toggles
// Order here controls both the toggles list and the "Active Layers" readout.
const ALL_MAP_LAYERS = [
  { key: 'latestVessels', label: 'Latest Vessel Positions', icon: MapPin, color: 'emerald', modes: ['Vessel Tracking'] },
  { key: 'track', label: 'Vessel Track', icon: Layers, color: 'indigo', modes: ['Vessel Tracking', 'Threat Zone', 'Relative Grid View'] },
  { key: 'velocityArrows', label: 'Direction arrows', icon: ArrowRight, color: 'indigo', modes: ['Vessel Tracking'] },
  { key: 'threatZones', label: 'Threat zones', icon: AlertCircle, color: 'red', modes: ['Vessel Tracking'] },
  // Ocean currents / vector field can be used in both analysis and vessel views
  { key: 'oceanCurrents', label: 'Ocean Currents', icon: Navigation, color: 'blue', modes: ['Ocean Analysis', 'Vessel Tracking', 'Relative Grid View'] },
  { key: 'threats', label: 'Threats', icon: AlertCircle, color: 'red', modes: ['Ocean Analysis', 'Vessel Tracking', 'Marker Clusters', 'Threat Zone', 'Relative Grid View'] },
  { key: 'exerciseScenario', label: 'Exercise scenario (Op Alpha, zones, CBU)', icon: Crosshair, color: 'violet', modes: ['Ocean Analysis', 'Vessel Tracking', 'Marker Clusters', 'Threat Zone', 'Relative Grid View'] },
];

// Helper to map layer colors to Tailwind CSS classes
const layerColorClasses = {
  blue: 'text-blue-600 dark:text-blue-400',
  red: 'text-red-600 dark:text-red-400',
  indigo: 'text-indigo-600',
  emerald: 'text-emerald-600 dark:text-emerald-300',
  orange: 'text-orange-600',
  violet: 'text-violet-600 dark:text-violet-400',
};

// Helper to map layer colors to button background classes
const layerButtonClasses = {
  blue: 'bg-blue-600 text-white',
  red: 'bg-red-600 text-white',
  indigo: 'bg-indigo-600 text-white',
  emerald: 'bg-emerald-600 text-white',
  orange: 'bg-orange-600 text-white',
  violet: 'bg-violet-600 text-white dark:bg-violet-600',
};


const ControlPanel = ({
  // Current state values
  isLoading = false,
  selectedArea = '',
  viewMode = 'Ocean Analysis', // Default view mode (actual value comes from hook)
  onViewModeChange,
  vesselMarkerDisplay = 'dots',
  onVesselMarkerDisplayChange,
  // selectedModel removed from UI but kept in props if needed elsewhere? 
  // Cleaning up unused props to avoid confusion
  selectedDepth = null,
  startDate,
  endDate,
  timeZone = 'UTC',
  currentFrame = 0,
  playbackSpeed = 0.1,
  loopMode = 'Repeat',

  // Layer visibility controls from mapLayerVisibility object
  mapLayerVisibility = {
    vesselTrail: true,
  },
  isSstHeatmapVisible = false,
  currentsVectorScale = 0.009,
  currentsColorBy = 'speed',

  // Data for dropdowns
  availableModels = [],
  availableDepths = [],
  availableDatasources = [],
  hasDepthData = true,
  hasModelData = true,
  totalFrames = 24,
  data = [],
  /** Chronological rows aligned with frame index (story clock). Falls back to `data` for time label. */
  frameTimelineData = null,
  dataLoaded = false,
  vesselsById = null,
  selectedVesselId = null,
  onSelectedVesselChange,
  vesselTimeRange = null,
  vesselPlaybackTime = null,
  onVesselPlaybackTimeChange,
  threatLevelFilter = 'all',
  onThreatLevelFilterChange,
  vesselTypeFilter = [],
  onVesselTypeFilterChange,

  // Callbacks
  onAreaChange,
  onModelChange,
  onDepthChange,
  onDateRangeChange,
  onTimeZoneChange,
  onSpeedChange,
  onLoopModeChange,
  onFrameChange,
  onReset,

  // Layer control callbacks
  onLayerToggle,
  toggleLayerAnimation,
  layerAnimationEnabled,
  onSstHeatmapToggle,
  onCurrentsScaleChange,
  onCurrentsColorChange,

  // New props for heatmap scale
  heatmapScale = 1,
  onHeatmapScaleChange,

  className = "",
  showAdvanced = false
}) => {
  const { isPlaying, togglePlay, notifyPlaybackReset } = useOcean();


  const handlePlayClick = (e) => {

    if (togglePlay) {
      togglePlay();
    } else {

    }
  };

  const [isCalendarOpen, setCalendarOpen] = useState(false);
  // Errors state removed as validation logic was simplified/removed with model dropdown
  const [showLayerControls, setShowLayerControls] = useState(true);
  const [showLayerToggles, setShowLayerToggles] = useState(false);
  const [vesselDropdownOpen, setVesselDropdownOpen] = useState(false);
  const vesselDropdownRef = useRef(null);
  const [vesselTypeDropdownOpen, setVesselTypeDropdownOpen] = useState(false);
  const vesselTypeDropdownRef = useRef(null);

  // Local state for the date picker to ensure state update and query trigger are coupled
  const [dateRangeValue, setDateRangeValue] = useState([startDate, endDate]);

  // Effect to sync local state if parent props change
  useEffect(() => {
    setDateRangeValue([startDate, endDate]);
  }, [startDate, endDate]);

  const dateFilterRef = useRef(null);

  // Close date range dropdown when clicking outside
  useEffect(() => {
    if (!isCalendarOpen) return;
    const handleClickOutside = (e) => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  // Close vessel dropdown when clicking outside
  useEffect(() => {
    if (!vesselDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (vesselDropdownRef.current && !vesselDropdownRef.current.contains(e.target)) {
        setVesselDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [vesselDropdownOpen]);

  // Close vessel type dropdown when clicking outside
  useEffect(() => {
    if (!vesselTypeDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (vesselTypeDropdownRef.current && !vesselTypeDropdownRef.current.contains(e.target)) {
        setVesselTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [vesselTypeDropdownOpen]);

  // Vessel type options from loaded data: distinct common_name / message_description_name / message_description_label / object_classification
  const availableVesselCommonNames = useMemo(() => {
    const set = new Set();
    const add = (row) => {
      const name = (row && (row.common_name ?? row.message_description_name ?? row.message_description_label ?? row.object_classification)) ?? '';
      const s = String(name).trim();
      if (s) set.add(s);
    };
    if (vesselsById && vesselsById.size > 0) {
      for (const rows of vesselsById.values()) {
        if (Array.isArray(rows)) rows.forEach(add);
      }
    }
    if (data && data.length > 0) {
      data.forEach(add);
    }
    return Array.from(set).sort();
  }, [vesselsById, data]);

  // Step 4: Vessels to display filtered by selected vessel type (Threat level + Vessel type above this list)
  // Step 5: vesselTypeFilter is string[]; empty = all types, else only types in array
  const vesselEntriesForDisplay = useMemo(() => {
    if (!vesselsById || vesselsById.size === 0) return [];
    const entries = Array.from(vesselsById.entries());
    const types = Array.isArray(vesselTypeFilter) ? vesselTypeFilter : [];
    if (types.length === 0) return entries;
    return entries.filter(([, rows]) => {
      const first = rows?.[0];
      const name = (first && (first.common_name ?? first.message_description_name ?? first.message_description_label ?? first.object_classification)) ?? '';
      return types.includes(String(name).trim());
    });
  }, [vesselsById, vesselTypeFilter]);

  // Log when user selects a specific vessel (for debugging)
  useEffect(() => {
    if (selectedVesselId && vesselsById && vesselsById.has(selectedVesselId)) {
      const rows = vesselsById.get(selectedVesselId);
      const first = rows?.[0];
      const label = first?.common_name || first?.object_classification || 'Unknown';
      // eslint-disable-next-line no-console
      console.log('Selected vessel:', {
        id: selectedVesselId,
        label: `${label} (${selectedVesselId})`,
        rowCount: rows?.length ?? 0,
        rows,
      });
    }
  }, [selectedVesselId, vesselsById]);

  // Available options - now dynamic from datasources
  const areaOptions = useMemo(() => {
    if (availableDatasources.length === 0) {
      return [{ value: '', label: 'Loading datasources...', disabled: true }];
    }
    return [
      { value: '', label: 'Select Datasource', disabled: true },
      ...availableDatasources.map(ds => ({
        value: ds.key,
        label: ds.label,
        disabled: false
      }))
    ];
  }, [availableDatasources]);

  // Logic for depth dropdown options
  const depthOptions = useMemo(() => {
    if (!availableDepths || availableDepths.length === 0) {
      return [{ value: '', label: 'Loading...', disabled: true }];
    }
    return availableDepths.map(depth => ({
      value: depth,
      label: depth === 0 ? '0 m (Surface)' : `${depth} m`,
      disabled: false
    }));
  }, [availableDepths]);



  // Model handling removed as requested

  const handleAreaChange = (e) => {
    const value = e.target.value;
    onAreaChange?.(value);
  };

  const handleDepthChange = (e) => {
    const value = Number(e.target.value);
    onDepthChange?.(value);
  };

  const handlePreviousFrame = () => {
    const prevFrame = currentFrame > 0 ? currentFrame - 1 : totalFrames - 1;
    onFrameChange?.(prevFrame);
  };

  const handleNextFrame = () => {
    const nextFrame = (currentFrame + 1) % totalFrames;
    onFrameChange?.(nextFrame);
  };



  const handleHeatmapScaleChange = (e) => {
    const value = Number(e.target.value);
    onHeatmapScaleChange?.(value);
  };



  const handleLayerToggle = (layerKey) => {
    if (onLayerToggle) {
      onLayerToggle(layerKey);
    } else {
      console.warn('ControlPanel - onLayerToggle function not provided!');
    }
  };

  const handlePulseToggle = () => {
    if (toggleLayerAnimation) {
      toggleLayerAnimation('threats');
    }
  };

  // This now only updates the local state
  const handleDateTimeChange = (value) => {
    setDateRangeValue(value);
  };

  // Commits the selected range to the parent and triggers refetch (Apply)
  const handleDateTimeConfirm = () => {
    const [start, end] = dateRangeValue || [null, null];
    onDateRangeChange?.({ startDate: start, endDate: end });
    setCalendarOpen(false);
  };

  const getFrameTimeDisplay = () => {
    const series = frameTimelineData && frameTimelineData.length > 0 ? frameTimelineData : data;
    const row = series[currentFrame];
    const t = row && (row.time ?? row.timestamp);
    if (t) {
      return new Date(t).toLocaleString();
    }
    return `Frame ${currentFrame + 1} of ${totalFrames}`;
  };

  const visibleMapLayers = useMemo(() => {
    return ALL_MAP_LAYERS.filter(layer => layer.modes.includes(viewMode));
  }, [viewMode]);

  const getActiveLayers = () => {
    return visibleMapLayers.filter(layer => mapLayerVisibility[layer.key]);
  };

  // Determine if any vector layer is active for scale slider
  const isAnyVectorLayerActive = useMemo(() => {
    const vectorKeys = ['oceanCurrents', 'windVelocity'];
    return vectorKeys.some(key => mapLayerVisibility[key]);
  }, [mapLayerVisibility]);

  // Determine if any heatmap layer is active for scale slider
  const isAnyHeatmapLayerActive = useMemo(() => {
    const heatmapKeys = ['temperature', 'salinity', 'pressure', 'ssh'];
    return heatmapKeys.some(key => mapLayerVisibility[key]);
  }, [mapLayerVisibility]);





  return (
    <div className={`bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 p-2 md:p-4 shadow-sm ${className} dark:bg-gradient-to-b dark:from-pink-900/10 dark:border-pink-500/20 dark:bg-slate-800 dark:to-purple-900/10`}>
      <div className="flex items-center justify-between mb-2 md:mb-4">
        <h2 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2 text-sm md:text-base dark:text-pink-300">
          <Activity className="w-4 h-4 md:w-5 md:h-5 text-blue-600 dark:text-blue-400" />
          Control Panel
        </h2>
        <div className="flex items-center gap-2">
          {isLoading && (
            <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 dark:text-cyan-400">
              <Loader className="w-3 h-3 animate-spin" />
              Loading...
            </div>
          )}
          {!dataLoaded && !isLoading && (
            <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-yellow-400 dark:text-yellow-400">
              <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse dark:bg-yellow-400"></div>
              Initializing
            </div>
          )}
        </div>
      </div>

      <div className={`grid gap-2 md:gap-4 mb-4 ${hasModelData && hasDepthData ? 'grid-cols-2 lg:grid-cols-4' : hasModelData || hasDepthData ? 'grid-cols-2 lg:grid-cols-3' : 'grid-cols-2'}`}>
        <div>
          <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400"><MapPin className="w-3 h-3" /> Datasource</label>
          <select value={selectedArea} onChange={handleAreaChange} className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-1 md:px-2 py-1 text-xs md:text-sm text-slate-700 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600">
            {areaOptions.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400"><Layers className="w-3 h-3" /> View Mode</label>
          <select
            value={viewMode}
            onChange={(e) => onViewModeChange?.(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-1 md:px-2 py-1 text-xs md:text-sm text-slate-700 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600"
          >
            <option value="Ocean Analysis">Ocean Analysis</option>
            <option value="Vessel Tracking">Vessel Tracking</option>
            <option value="Marker Clusters">Marker Clusters</option>
            <option value="Threat Zone">Threat Zone</option>
            <option value="Relative Grid View">Relative Grid View</option>
          </select>
        </div>

        <div ref={dateFilterRef} className={hasModelData && hasDepthData ? "col-span-2 lg:col-span-1" : ""} style={{ position: 'relative' }}>
          <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400"><Calendar className="w-3 h-3" /> Date/Time Range</label>

          <button
            onClick={() => setCalendarOpen(!isCalendarOpen)}
            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-xs md:text-sm text-left truncate text-slate-700 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600"
            disabled={!dataLoaded}
          >
            {startDate && endDate ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}` : 'Select Range'}
          </button>

          {isCalendarOpen && (
            <div className="absolute top-full mt-2 z-50 bg-white dark:bg-slate-800 dark:border dark:border-slate-600 p-2 rounded-md shadow-lg">
              <DateTimeRangePicker
                onChange={handleDateTimeChange}
                value={dateRangeValue}
                disabled={!dataLoaded}
                className="text-slate-900 dark:text-white"
              />
              <div className="mt-2 text-right">
                <button
                  onClick={handleDateTimeConfirm}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 dark:bg-pink-600 dark:hover:bg-pink-700"
                  disabled={!dateRangeValue || !dateRangeValue[0] || !dateRangeValue[1] || isLoading}
                  title="Load data for the selected date range"
                >
                  {isLoading ? 'Loading…' : 'Apply'}
                </button>
              </div>
            </div>
          )}
        </div>

        {hasDepthData && (
          <div>
            <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400">
              <Gauge className="w-3 h-3" />
              Depth (m)
            </label>
            <select
              value={selectedDepth ?? ''}
              onChange={handleDepthChange}
              className={`w-full bg-slate-50 dark:bg-slate-800 border rounded px-1 md:px-2 py-1 text-xs md:text-sm text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:border-slate-600`}
              disabled={!dataLoaded || !availableDepths.length}
            >
              {depthOptions.map(depth => <option key={depth.value} value={depth.value} disabled={depth.disabled}>{depth.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {mapLayerVisibility.exerciseScenario &&
        (viewMode === 'Vessel Tracking' || viewMode === 'Marker Clusters') && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-600 dark:text-slate-300">Vessel markers</span>
          <div className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden shadow-sm">
            <button
              type="button"
              onClick={() => onVesselMarkerDisplayChange?.('dots')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                vesselMarkerDisplay === 'dots'
                  ? 'bg-blue-600 text-white dark:bg-pink-600'
                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              Dots
            </button>
            <button
              type="button"
              onClick={() => onVesselMarkerDisplayChange?.('icons')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-300 dark:border-slate-600 ${
                vesselMarkerDisplay === 'icons'
                  ? 'bg-blue-600 text-white dark:bg-pink-600'
                  : 'bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              Icons
            </button>
          </div>
        </div>
      )}

      {/* Layer Controls Section */}
      <div className="mb-4 border-t border-slate-200 dark:border-slate-700 pt-3 dark:border-slate-600">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1 dark:text-slate-300">
            <Settings className="w-3 h-3" />
            Map Controls
          </h3>
          <button
            onClick={() => setShowLayerControls(!showLayerControls)}
            className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 dark:text-slate-400 dark:hover:text-slate-300"
          >
            {showLayerControls ? 'Hide' : 'Show'}
          </button>
        </div>

        {showLayerControls && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

            {/* Column 1: Layer Toggles (now nested) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1 dark:text-slate-300">
                  <Map className="w-3 h-3" />
                  Map Layers
                </h4>
                <button
                  onClick={() => setShowLayerToggles(!showLayerToggles)}
                  className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 dark:text-slate-400 dark:hover:text-slate-300"
                >
                  {showLayerToggles ? 'Hide' : 'Show'}
                </button>
              </div>

              {showLayerToggles && (
                <div className="space-y-2">
                  {visibleMapLayers.map(layer => (
                    <div key={layer.key} className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 dark:text-slate-300">
                        <layer.icon className="w-3 h-3" />
                        {layer.label}
                      </label>
                      <button
                        onClick={() => handleLayerToggle(layer.key)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${mapLayerVisibility[layer.key]
                          ? layerButtonClasses[layer.color]
                          : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600'
                          }`}
                        disabled={!dataLoaded}
                      >
                        {mapLayerVisibility[layer.key] ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {mapLayerVisibility[layer.key] ? 'On' : 'Off'}
                      </button>
                    </div>
                  ))}
                  {viewMode === 'Vessel Tracking' && (
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-600 space-y-2">
                      <div>
                        <label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">Threat level</label>
                        <div className="relative">
                          <select
                            value={threatLevelFilter}
                            onChange={(e) => onThreatLevelFilterChange?.(e.target.value)}
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 pr-6 text-xs text-slate-700 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600 appearance-none"
                            disabled={!dataLoaded}
                          >
                            <option value="all">All</option>
                            <option value="no-threat">Low threat only</option>
                            <option value="threat">High threat only</option>
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-300" aria-hidden />
                        </div>
                      </div>
                      <div ref={vesselTypeDropdownRef}>
                        <label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">Vessel type</label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setVesselTypeDropdownOpen((open) => !open)}
                            disabled={!dataLoaded}
                            className="w-full flex items-center gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600 text-left disabled:opacity-50"
                          >
                            {(() => {
                              const types = Array.isArray(vesselTypeFilter) ? vesselTypeFilter : [];
                              if (types.length === 0) {
                                return (
                                  <>
                                    <Layers className="w-4 h-4 flex-shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                                    <span className="truncate">All types</span>
                                  </>
                                );
                              }
                              if (types.length === 1) {
                                const name = types[0];
                                const IconC = getVesselIconComponent(name);
                                const displayName = String(name).split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                                return (
                                  <>
                                    <IconC className="w-4 h-4 flex-shrink-0 text-amber-500 dark:text-yellow-500" aria-hidden />
                                    <span className="truncate">{displayName}</span>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <Layers className="w-4 h-4 flex-shrink-0 text-amber-500 dark:text-yellow-500" aria-hidden />
                                  <span className="truncate">{types.length} types</span>
                                </>
                              );
                            })()}
                            <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-slate-400 dark:text-slate-300" aria-hidden />
                          </button>
                          {vesselTypeDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-0.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded shadow-lg z-50 max-h-48 overflow-y-auto">
                              <label className="flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!Array.isArray(vesselTypeFilter) || vesselTypeFilter.length === 0}
                                  onChange={() => { onVesselTypeFilterChange?.([]); }}
                                  className="rounded border-slate-300 dark:border-slate-500"
                                />
                                <Layers className="w-4 h-4 flex-shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                                <span>All types</span>
                              </label>
                              {availableVesselCommonNames.map((name) => {
                                const IconC = getVesselIconComponent(name);
                                const displayName = String(name).split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                                const types = Array.isArray(vesselTypeFilter) ? vesselTypeFilter : [];
                                const isChecked = types.includes(name);
                                return (
                                  <label
                                    key={name}
                                    className={`flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer ${isChecked ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {
                                        const next = isChecked ? types.filter((t) => t !== name) : [...types, name];
                                        onVesselTypeFilterChange?.(next);
                                      }}
                                      className="rounded border-slate-300 dark:border-slate-500"
                                    />
                                    <IconC className="w-4 h-4 flex-shrink-0 text-amber-500 dark:text-yellow-500" aria-hidden />
                                    <span className="truncate">{displayName}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {viewMode === 'Vessel Tracking' && vesselsById && vesselsById.size > 0 && (
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-600" ref={vesselDropdownRef}>
                      <label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">Vessels to display</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setVesselDropdownOpen((open) => !open)}
                          disabled={!dataLoaded}
                          className="w-full flex items-center gap-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600 text-left disabled:opacity-50"
                        >
                          {selectedVesselId === VESSEL_DISPLAY_NONE ? (
                            <>
                              <EyeOff className="w-4 h-4 flex-shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                              <span className="truncate">No vessels</span>
                            </>
                          ) : !selectedVesselId ? (
                            <>
                              <MapPin className="w-4 h-4 flex-shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                              <span className="truncate">All vessels</span>
                            </>
                          ) : (() => {
                            const rows = vesselsById.get(selectedVesselId);
                            const first = rows?.[0];
                            const name = first?.common_name || first?.object_classification || 'Unknown';
                            const IconC = getVesselIconComponent(name);
                            const idx = vesselEntriesForDisplay.findIndex(([id]) => id === selectedVesselId);
                            const displayIndex = idx >= 0 ? idx + 1 : 1;
                            const label = `Vessel ${displayIndex} - ${String(name).split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')} (${selectedVesselId})`;
                            return (
                              <>
                                <IconC className="w-4 h-4 flex-shrink-0 text-amber-500 dark:text-yellow-500" aria-hidden />
                                <span className="truncate">{label}</span>
                              </>
                            );
                          })()}
                          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-slate-400 dark:text-slate-300" aria-hidden />
                        </button>
                          {vesselDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-0.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded shadow-lg z-50 max-h-48 overflow-y-auto">
                              <button
                                type="button"
                                onClick={() => { onSelectedVesselChange?.(VESSEL_DISPLAY_NONE); setVesselDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-slate-100 dark:hover:bg-slate-700 ${selectedVesselId === VESSEL_DISPLAY_NONE ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'}`}
                              >
                                <EyeOff className="w-4 h-4 flex-shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                                No vessels
                              </button>
                              <button
                                type="button"
                                onClick={() => { onSelectedVesselChange?.(null); setVesselDropdownOpen(false); }}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-slate-100 dark:hover:bg-slate-700 ${!selectedVesselId ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'}`}
                              >
                                <MapPin className="w-4 h-4 flex-shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
                                All vessels
                              </button>
                            {vesselEntriesForDisplay.map(([id, rows], index) => {
                              const first = rows[0];
                              const name = first?.common_name || first?.object_classification || 'Unknown';
                              const IconC = getVesselIconComponent(name);
                              const displayName = String(name).split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                              const label = `Vessel ${index + 1} - ${displayName} (${id})`;
                              const isSelected = selectedVesselId === id;
                              return (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => { onSelectedVesselChange?.(id); setVesselDropdownOpen(false); }}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-slate-100 dark:hover:bg-slate-700 ${isSelected ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'}`}
                                >
                                  <IconC className="w-4 h-4 flex-shrink-0 text-amber-500 dark:text-yellow-500" aria-hidden />
                                  <span className="truncate">{label}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {viewMode === 'Vessel Tracking' && vesselTimeRange && vesselPlaybackTime != null && onVesselPlaybackTimeChange && (
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-600 space-y-1.5">
                      <label className="block text-xs text-slate-600 dark:text-slate-300">Playback time</label>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate" title={new Date(vesselPlaybackTime).toISOString()}>
                        {new Date(vesselPlaybackTime).toLocaleString()}
                      </div>
                      <input
                        type="range"
                        min={vesselTimeRange.minMs}
                        max={vesselTimeRange.maxMs}
                        step={1000}
                        value={vesselPlaybackTime}
                        onChange={(e) => onVesselPlaybackTimeChange(Number(e.target.value))}
                        className="w-full h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          notifyPlaybackReset?.();
                          onVesselPlaybackTimeChange(vesselTimeRange.minMs);
                        }}
                        className="flex items-center gap-1 w-full justify-center px-2 py-1 rounded text-xs bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Reset to start
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Column 2: Layer Controls */}
            <div className="space-y-4">
              {/* Pulse Effect Toggle (Only if Threats are active) */}
              <div className="flex items-center justify-between">
                <label className={`text-xs ${mapLayerVisibility.threats ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
                  Pulse Effect
                </label>
                <button
                  onClick={handlePulseToggle}
                  className={`px-2 py-1 rounded text-xs transition-colors ${mapLayerVisibility.threats && layerAnimationEnabled?.threats
                    ? 'bg-red-600 text-white'
                    : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                    }`}
                  disabled={!mapLayerVisibility.threats}
                >
                  {layerAnimationEnabled?.threats ? 'On' : 'Off'}
                </button>
              </div>

              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400">Currents/Wind Scale</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="0.0001"
                    max="0.01"
                    step="0.0001"
                    value={currentsVectorScale}
                    onChange={(e) => onCurrentsScaleChange?.(Number(e.target.value))}
                    className="flex-1 accent-blue-500 disabled:opacity-50"
                    disabled={!dataLoaded || !isAnyVectorLayerActive}
                  />
                  <span className="text-xs text-slate-500 dark:text-slate-400 w-16 dark:text-slate-400">
                    {(currentsVectorScale * 1000).toFixed(1)}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 dark:text-slate-400">Heatmap Scale</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="0.1"
                    max="5"
                    step="0.1"
                    value={heatmapScale}
                    onChange={handleHeatmapScaleChange}
                    className="flex-1 accent-red-500 disabled:opacity-50"
                    disabled={!dataLoaded || !isAnyHeatmapLayerActive}
                  />
                  <span className="text-xs text-slate-500 dark:text-slate-400 w-16 dark:text-slate-400">
                    {heatmapScale}x
                  </span>
                </div>
              </div>

              {/* <label className="block text-xs text-slate-400">Color Mode</label>
              <select
                value={currentsColorBy}
                onChange={handleCurrentsColorChange}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-xs dark:bg-slate-700 dark:border-slate-600"
                disabled={!dataLoaded || !isAnyVectorLayerActive}
              >
                {currentsColorOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select> */}
            </div>

            {/* Column 3: Layer Info */}
            <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1 dark:text-slate-400">
              <div>Active Layers:</div>
              <div className="pl-2 space-y-0.5">
                {getActiveLayers().map(layer => (
                  <div key={layer.key} className={layerColorClasses[layer.color]}>
                    {layer.label}
                  </div>
                ))}
                {getActiveLayers().length === 0 && (
                  <div className="text-slate-400 dark:text-slate-500">No layers active</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 mb-4">
        <div>
          <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 dark:text-slate-400">Animation</label>
          <div className="flex gap-1">
            <button onClick={handlePreviousFrame} className="p-1 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-600 rounded text-slate-600 dark:text-slate-300 disabled:opacity-50 dark:bg-slate-600 dark:hover:bg-slate-700" disabled={!dataLoaded || (viewMode === 'Vessel Tracking' ? !vesselTimeRange : totalFrames <= 1)}><SkipBack className="w-3 h-3" /></button>
            <button onClick={handlePlayClick} className="flex-1 flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-2 rounded text-xs transition-colors disabled:opacity-50 dark:bg-pink-600 dark:hover:bg-pink-700" disabled={!dataLoaded || (viewMode === 'Vessel Tracking' ? !vesselTimeRange : totalFrames <= 1)}>
              {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            <button onClick={handleNextFrame} className="p-1 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-600 rounded text-slate-600 dark:text-slate-300 disabled:opacity-50 dark:bg-slate-600 dark:hover:bg-slate-700" disabled={!dataLoaded || (viewMode === 'Vessel Tracking' ? !vesselTimeRange : totalFrames <= 1)}><SkipForward className="w-3 h-3" /></button>
            <button onClick={onReset} className="p-1 bg-slate-200 dark:bg-slate-600 hover:bg-slate-300 dark:hover:bg-slate-600 rounded text-slate-600 dark:text-slate-300 disabled:opacity-50 dark:bg-slate-600 dark:hover:bg-slate-700" disabled={!dataLoaded}><RotateCcw className="w-3 h-3" /></button>
          </div>
        </div>

        <div>
          <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 dark:text-slate-400">Speed: {playbackSpeed}x</label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0.1"
              max="5"
              step="0.1"
              value={playbackSpeed}
              onChange={(e) => onSpeedChange?.(Number(e.target.value))}
              className="flex-1 accent-blue-500 disabled:opacity-50"
              disabled={!dataLoaded}
            />
            <div className="flex gap-1">
              {[1, 2, 3, 5].map((speed) => (
                <button
                  key={speed}
                  onClick={() => onSpeedChange?.(speed)}
                  className={`px-1 py-0.5 rounded text-xs ${playbackSpeed === speed ? 'bg-blue-600 text-white dark:bg-blue-500' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'}`}
                  disabled={!dataLoaded}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between text-xs text-slate-500 dark:text-slate-400 gap-2 dark:text-slate-400">
        <div className="flex items-center gap-4">
          <span>Frame: {currentFrame + 1}/{totalFrames}</span>
          <span>Loop: {loopMode}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3" />
          <span>{getFrameTimeDisplay()}</span>
        </div>
      </div>

    </div >
  );
};

export default ControlPanel;